/**
 * Boolean ops on closed CONSTRUCTION shapes (#764).
 *
 * The geometry lives in `shapeBooleanCore`; this module owns the construction
 * half — which lines can be operands, and what replacing them does to the Parts
 * list, group membership and constraints. `leatherBoolean` is the same job for
 * parts, cut-outs and stitch loops.
 *
 * ## Why the operand order is the Parts-list order
 *
 * Construction lines have no fill and no z-occlusion, so Illustrator's
 * front / back framing has nothing to hang on here. Selection click order
 * isn't dependable either — a marquee or a Select-All produces a set, not a
 * sequence. So the rule is the one thing that IS visible and stable: the
 * order the shapes appear in the Parts list. For Subtract, the topmost
 * selected shape is the one that keeps its material and every other selected
 * shape is taken out of it.
 *
 * ## What is not an operand
 *
 * Open lines (a polyline has no inside), degenerate shapes with no area, and
 * DERIVED lines — offset children and mirror children. A derived line's
 * geometry is recomputed from its source on every commit, so consuming one
 * would either be undone by the next derive pass or silently orphan the
 * relationship. Split excludes them for the same reason
 * (`canSplitConstructionLine`).
 */
import type { EditorContext } from "../tools/EditorContext";
import {
  nextConstructionName,
  projectConstructionGroups,
  projectConstructionLines,
  type ConstructionLine,
  type Project,
} from "./Project";
import {
  projectConstraints,
  pruneConstraintsForDeletedLines,
} from "./constraints";
import { newId } from "./ids";
import {
  SHAPE_BOOLEAN_OPS,
  canShapeBooleanWith,
  computeShapeBoolean,
  isClosedShapePath,
  MIN_OPERANDS,
  type ShapeBooleanOp,
} from "./shapeBooleanCore";

/** True when a construction line can take part in a boolean op. */
export function isShapeBooleanOperand(
  line: ConstructionLine,
  scope: paper.PaperScope,
): boolean {
  // Derived geometry isn't the line's to give away — see the module docstring.
  if (line.offsetSource || line.mirrorSource) return false;
  return isClosedShapePath(line.pathData, scope);
}

/** The operand lines for a selection, in PARTS-LIST order (not selection
 *  order) — see the module docstring on why that is the order that decides
 *  Subtract's base. */
export function shapeBooleanOperands(
  project: Project,
  selectedLineIds: readonly string[],
  scope: paper.PaperScope,
): ConstructionLine[] {
  if (selectedLineIds.length === 0) return [];
  const selected = new Set(selectedLineIds);
  return projectConstructionLines(project).filter(
    (l) => selected.has(l.id) && isShapeBooleanOperand(l, scope),
  );
}

/** True when the current selection can feed `op`. Drives the panel buttons'
 *  `disabled` state. */
export function canShapeBooleanSelection(
  ctx: EditorContext,
  op: ShapeBooleanOp,
): boolean {
  const ids = ctx.scene.getSelection().constructionIds ?? [];
  if (ids.length < MIN_OPERANDS) return false;
  const operands = shapeBooleanOperands(
    ctx.history.state,
    ids,
    ctx.scene.getPaperScope(),
  );
  return canShapeBooleanWith(operands.length, op);
}

/**
 * Selection-driven boolean op. The operand lines are replaced by the
 * result, which lands in the first operand's slot in the Parts list and
 * inherits its group membership.
 *
 * Visibility inherits the most-restrictive setting, as Join does: if any
 * operand was hidden the result is hidden, so a boolean op can't reveal
 * geometry the user had put away.
 *
 * Constraints touching a consumed line are dropped. Per-anchor and per-curve
 * refs carry an index into a `pathData` that no longer exists — a boolean op
 * rebuilds the segment topology from scratch, so there is no honest way to
 * re-target them, and silently pointing them somewhere new would be worse
 * than losing them. Same reasoning as Split and Join. Offset / mirror children
 * of a consumed line are pruned by the derive pass on the next commit, which
 * already drops derived lines whose source has vanished.
 *
 * Returns false — committing nothing — when the op produces no area.
 */
export function shapeBooleanSelection(
  ctx: EditorContext,
  op: ShapeBooleanOp,
): boolean {
  const scope = ctx.scene.getPaperScope();
  const ids = ctx.scene.getSelection().constructionIds ?? [];
  const operands = shapeBooleanOperands(ctx.history.state, ids, scope);
  if (!canShapeBooleanWith(operands.length, op)) return false;
  const built = computeShapeBoolean(
    op,
    operands.map((l) => l.pathData),
    scope,
  );
  if (!built) return false;

  const spec = SHAPE_BOOLEAN_OPS.find((s) => s.op === op)!;
  const consumed = new Set(operands.map((l) => l.id));
  const anyHidden = operands.some((l) => l.visible === false);
  const newIds = built.map(() => newId("con"));

  ctx.commit(`${spec.label} shapes`, (proj0) => {
    let nextProj = proj0;
    const nextLines: ConstructionLine[] = [];
    let inserted = false;
    for (const line of projectConstructionLines(proj0)) {
      if (!consumed.has(line.id)) {
        nextLines.push(line);
        continue;
      }
      // Every operand drops out; the results take the slot of the first one
      // so the Parts list doesn't reshuffle around the user.
      if (inserted) continue;
      inserted = true;
      for (let i = 0; i < built.length; i++) {
        nextLines.push({
          id: newIds[i]!,
          name: nextConstructionName(
            { ...nextProj, constructionLines: nextLines },
            spec.resultModifier,
          ),
          pathData: built[i]!,
          kind: "polygon",
          ...(anyHidden ? { visible: false } : {}),
          // Deliberately no `primitive` and no `smoothAnchors`: a united
          // rectangle is not a rectangle any more, and the anchor indices a
          // lock is stored against belong to geometry the op just replaced.
        });
      }
    }
    nextProj = { ...nextProj, constructionLines: nextLines };

    // Group rewrite: substitute the consumed ids with the result ids at the
    // first occurrence and drop the rest, so a group that held the operands
    // holds the results and doesn't list any of them twice.
    const groups = projectConstructionGroups(nextProj);
    if (groups.length > 0) {
      const nextGroups = groups.map((g) => {
        if (!g.lineIds.some((lid) => consumed.has(lid))) return g;
        const lineIds: string[] = [];
        let substituted = false;
        for (const lid of g.lineIds) {
          if (!consumed.has(lid)) {
            lineIds.push(lid);
            continue;
          }
          if (substituted) continue;
          substituted = true;
          lineIds.push(...newIds);
        }
        return { ...g, lineIds };
      });
      nextProj = { ...nextProj, constructionGroups: nextGroups };
    }

    const pruned = pruneConstraintsForDeletedLines(
      projectConstraints(nextProj),
      consumed,
    );
    if (pruned !== projectConstraints(nextProj)) {
      nextProj = { ...nextProj, constraints: pruned };
    }
    return nextProj;
  });

  ctx.setSelection({
    pieceId: null,
    decorationId: null,
    constructionIds: newIds,
    constructionGroupIds: [],
  });
  return true;
}
